import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { MetricsApiService, ActiveUser, Question, FeedbackComment } from '~/services/metrics-api.service';
import { MultiSelectDropdownComponent } from '~/components/multi-select-dropdown/multi-select-dropdown.component';
import jsPDF from 'jspdf';

@Component({
  selector: 'app-metrics',
  standalone: true,
  imports: [CommonModule, FormsModule, HighchartsChartModule, MultiSelectDropdownComponent],
  templateUrl: './metrics.html',
  styleUrls: ['./metrics.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MetricsComponent implements OnInit {
  
  // Date range state
  fromDate: string = '';
  toDate: string = '';
  todayDate: string = ''; // Today's date for max constraint

  // Loading and error states
  isLoading: boolean = false;
  error: string | null = null;

  // KPI data
  kpiCards: any[] = [];

  // Graph data placeholder
  graphData: any[] = [];

  // Modal states
  activeModal: string | null = null;
  searchQuery: string = '';
  currentPage: number = 1;
  itemsPerPage: number = 15;

  // Modal data - using proper interfaces
  activeUsers: ActiveUser[] = [];
  questionsAsked: Question[] = [];
  feedbackComments: FeedbackComment[] = [];

  // Track which feedback answers are expanded
  expandedAnswers: { [key: string]: boolean } = {};

  // Modal loading states
  modalLoading: boolean = false;
  modalError: string | null = null;

  // Filter states - changed to arrays for multi-select
  selectedUsers: string[] = [];
  selectedRatings: any[] = [];  // Use 'any' type to handle string/number conversion from form binding

  // Debounce timers
  private questionsFilterDebounceTimer: any = null;
  private feedbackFilterDebounceTimer: any = null;
  private readonly DEBOUNCE_DELAY = 800; // 800ms debounce delay

  // Pagination states
  totalQuestionsCount: number = 0;
  totalFeedbackCount: number = 0;
  currentQuestionsPage: number = 1;
  currentFeedbackPage: number = 1;

  // Expose Math to template
  Math = Math;
  
  // Highcharts options
  Highcharts = Highcharts;
  dailyActiveUsersChartOptions: Highcharts.Options = {};

  constructor(
    private router: Router,
    private metricsApi: MetricsApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Set default date range: last 3 months
    this.setDefaultDateRange();
    // Fetch data with default date range
    this.loadMetrics();
  }

  /**
   * Set default date range to last 3 months from today
   */
  setDefaultDateRange(): void {
    const today = new Date();
    // Calculate 3 months ago more accurately
    let threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    this.fromDate = this.formatDateForInput(threeMonthsAgo);
    this.toDate = this.formatDateForInput(today);
    this.todayDate = this.formatDateForInput(today); // Set max constraint to today
  }

  /**
   * Format date for HTML input type="date"
   */
  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Load metrics data from API
   */
  private loadMetrics(): void {
    this.isLoading = true;
    this.error = null;
    this.cdr.markForCheck();

    this.metricsApi.getMetricsDashboard(this.fromDate, this.toDate, false).subscribe({
      next: (response) => {
        if (response.status === 'success' && response.data) {
          this.updateKPICards(response.data);
          this.graphData = response.data.daily_active_users;
          this.initializeDailyActiveUsersChart();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.error = 'Failed to load metrics data. Please try again.';
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Update KPI cards with real data from API
   */
  private updateKPICards(data: any): void {
    const summary = data.summary;
    const sentiment = data.feedback_sentiment;

    this.kpiCards = [
      {
        title: 'Total Active Users',
        value: summary.total_active_users.toString(),
        description: 'Unique users active in the selected range',
        icon: 'users'
      },
      {
        title: 'Total Questions Asked',
        value: summary.total_questions_asked.toString(),
        description: 'Total chatbot questions in the selected range',
        icon: 'question'
      },
      {
        title: 'Questions with Feedback',
        value: summary.questions_with_feedback.toString(),
        description: 'Questions that received user feedback',
        icon: 'feedback'
      },
      {
        title: 'Feedback Sentiment',
        value: `${sentiment.likes} Likes / ${sentiment.dislikes} Dislikes / ${sentiment.neutral} Neutral`,
        description: 'Likes / Dislikes / Neutral feedback breakdown',
        icon: 'chart'
      }
    ];
  }

  /**
   * Handle date range apply with validation
   */
  applyDateRange(): void {
    // Validate that fromDate and toDate are selected
    if (!this.fromDate || !this.toDate) {
      this.error = 'Please select both From and To dates.';
      this.cdr.markForCheck();
      return;
    }

    // Validate that toDate is not earlier than fromDate
    const fromDateObj = new Date(this.fromDate);
    const toDateObj = new Date(this.toDate);

    if (toDateObj < fromDateObj) {
      this.error = 'The "To" date must be equal to or later than the "From" date.';
      this.toDate = this.fromDate; // Reset toDate to fromDate
      this.cdr.markForCheck();
      return;
    }

    // Clear any previous errors and load metrics
    this.error = null;
    this.loadMetrics();
  }

  /**
   * Initialize Daily Active Users chart
   */
  private initializeDailyActiveUsersChart(): void {
    if (!this.graphData || this.graphData.length === 0) {
      return;
    }

    const dates = this.graphData.map(d => d.date);
    const userCounts = this.graphData.map(d => d.user_count);

    this.dailyActiveUsersChartOptions = {
      chart: {
        type: 'area',
        backgroundColor: 'transparent',
        height: 350,
        width: null
      },
      title: {
        text: ''
      },
      xAxis: {
        categories: dates,
        crosshair: true,
        labels: {
          style: {
            color: '#666'
          }
        }
      },
      yAxis: {
        title: {
          text: 'Active Users',
          style: {
            color: '#666'
          }
        },
        labels: {
          style: {
            color: '#666'
          }
        }
      },
      plotOptions: {
        area: {
          stacking: 'normal',
          lineColor: '#1f77d4',
          fillOpacity: 0.3,
          color: '#1f77d4',
          animation: true,
          marker: {
            enabled: true,
            radius: 3
          }
        },
        series: {
          lineWidth: 2,
          states: {
            hover: {
              lineWidth: 3
            }
          }
        }
      },
      series: [
        {
          name: 'Active Users',
          data: userCounts,
          color: '#1f77d4',
          type: 'spline'
        } as any
      ],
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom'
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500
            },
            chartOptions: {
              chart: {
                height: 250
              }
            }
          }
        ]
      }
    };

    this.cdr.markForCheck();
  }

  /**
   * Navigate back to prompt page
   */
  goBack(): void {
    this.router.navigate(['/prompt']);
  }

  /**
   * Open modal and load data from API
   */
  openModal(modalType: string | null): void {
    if (!modalType) return;
    
    this.activeModal = modalType;
    this.searchQuery = '';
    this.currentPage = 1;
    this.currentQuestionsPage = 1;
    this.currentFeedbackPage = 1;
    this.selectedUsers = [];
    this.selectedRatings = [];
    this.modalLoading = true;
    this.modalError = null;

    // Load data based on modal type
    if (modalType === 'users') {
      this.loadActiveUsers();
    } else if (modalType === 'questions') {
      // Load active users for dropdown if not already loaded
      if (this.activeUsers.length === 0) {
        this.loadActiveUsers();
      }
      this.loadQuestionsPage(1);
    } else if (modalType === 'feedback') {
      // Load active users for dropdown if not already loaded
      if (this.activeUsers.length === 0) {
        this.loadActiveUsers();
      }
      this.loadFeedbackPage(1);
    }
  }

  /**
   * Load active users from API (load all users at once)
   */
  private loadActiveUsers(): void {
    this.metricsApi.getAllActiveUsers(this.fromDate, this.toDate, false).subscribe({
      next: (users) => {
        this.activeUsers = users;
        this.modalLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.modalError = 'Failed to load active users';
        this.modalLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load questions page from API with optional filters
   */
  private loadQuestionsPage(page: number): void {
    this.modalLoading = true;
    this.metricsApi.getQuestions(
      this.fromDate, 
      this.toDate, 
      page, 
      15,
      false,
      this.selectedUsers.length > 0 ? this.selectedUsers : undefined
    ).subscribe({
      next: (response) => {
        // Validate response has required fields and actual data before processing
        if (response && response.data !== undefined && response.pagination && response.pagination.total_count !== undefined) {
          this.questionsAsked = response.data;
          this.totalQuestionsCount = response.pagination.total_count;
          this.currentQuestionsPage = page;
          this.modalLoading = false;
        } else {
          // If response is incomplete/empty, don't update state or hide loader
          // Keep loader visible until valid data arrives
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.modalError = 'Failed to load questions';
        this.modalLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load feedback page from API with optional filters
   */
  private loadFeedbackPage(page: number): void {
    this.modalLoading = true;
    this.metricsApi.getFeedback(
      this.fromDate, 
      this.toDate, 
      page, 
      15,
      false,
      this.selectedUsers.length > 0 ? this.selectedUsers : undefined,
      this.selectedRatings.length > 0 ? this.selectedRatings : undefined
    ).subscribe({
      next: (response) => {
        // Validate response has required fields and actual data before processing
        if (response && response.data !== undefined && response.pagination && response.pagination.total_count !== undefined) {
          this.feedbackComments = response.data;
          this.totalFeedbackCount = response.pagination.total_count;
          this.currentFeedbackPage = page;
          this.modalLoading = false;
        } else {
          // If response is incomplete/empty, don't update state or hide loader
          // Keep loader visible until valid data arrives
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.modalError = 'Failed to load feedback';
        this.modalLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Navigate to next page
   */
  nextQuestionsPage(): void {
    const totalPages = this.getTotalQuestionsPages();
    if (this.currentQuestionsPage < totalPages) {
      this.loadQuestionsPage(this.currentQuestionsPage + 1);
    }
  }

  /**
   * Navigate to previous page
   */
  previousQuestionsPage(): void {
    if (this.currentQuestionsPage > 1) {
      this.loadQuestionsPage(this.currentQuestionsPage - 1);
    }
  }

  /**
   * Navigate to next feedback page
   */
  nextFeedbackPage(): void {
    const totalPages = this.getTotalFeedbackPages();
    if (this.currentFeedbackPage < totalPages) {
      this.loadFeedbackPage(this.currentFeedbackPage + 1);
    }
  }

  /**
   * Navigate to previous feedback page
   */
  previousFeedbackPage(): void {
    if (this.currentFeedbackPage > 1) {
      this.loadFeedbackPage(this.currentFeedbackPage - 1);
    }
  }

  /**
   * Get total pages for questions from API response
   */
  getTotalQuestionsPages(): number {
    return Math.max(1, Math.ceil(this.totalQuestionsCount / 15));
  }

  /**
   * Get total pages for feedback from API response
   */
  getTotalFeedbackPages(): number {
    return Math.max(1, Math.ceil(this.totalFeedbackCount / 15));
  }

  /**
   * Close modal
   */
  closeModal(): void {
    this.activeModal = null;
    this.searchQuery = '';
    this.modalError = null;
  }

  /**
   * Handle questions filter change - reload page 1 with new filters (called after debounce)
   */
  private onQuestionsFilterChangeDebounced(): void {
    this.currentQuestionsPage = 1;
    this.loadQuestionsPage(1);
  }

  /**
   * Handle questions user filter change from dropdown with debounce
   */
  onQuestionsUsersSelected(selectedUserIds: string[]): void {
    this.selectedUsers = selectedUserIds;
    
    // Clear existing debounce timer
    if (this.questionsFilterDebounceTimer) {
      clearTimeout(this.questionsFilterDebounceTimer);
    }
    
    // Set new debounce timer
    this.questionsFilterDebounceTimer = setTimeout(() => {
      this.onQuestionsFilterChangeDebounced();
    }, this.DEBOUNCE_DELAY);
  }

  /**
   * Handle feedback filter change - reload page 1 with new filters (called after debounce)
   */
  private onFeedbackFilterChangeDebounced(): void {
    // Convert rating strings to numbers for API (don't overwrite component state)
    const ratingsAsNumbers = this.selectedRatings
      .filter(r => r !== '') // Remove empty string if present
      .map(r => parseInt(r as any, 10))
      .filter(r => !isNaN(r)); // Remove NaN values
    
    this.currentFeedbackPage = 1;
    
    // Call API with converted numbers for ratings parameter
    this.loadFeedbackPageWithFilters(1, this.selectedUsers, ratingsAsNumbers);
  }

  /**
   * Load feedback page with specific filter values (used by debounced filter changes)
   */
  private loadFeedbackPageWithFilters(page: number, userIds: string[], ratings: number[]): void {
    this.modalLoading = true;
    this.metricsApi.getFeedback(
      this.fromDate,
      this.toDate,
      page,
      15,
      false,
      userIds.length > 0 ? userIds : undefined,
      ratings.length > 0 ? ratings : undefined
    ).subscribe({
      next: (response) => {
        // Validate response has required fields and actual data before processing
        if (response && response.data !== undefined && response.pagination && response.pagination.total_count !== undefined) {
          this.feedbackComments = response.data;
          this.totalFeedbackCount = response.pagination.total_count;
          this.currentFeedbackPage = page;
          this.modalLoading = false;
        } else {
          // If response is incomplete/empty, don't update state or hide loader
          // Keep loader visible until valid data arrives
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.feedbackComments = [];
        this.totalFeedbackCount = 0;
        this.modalLoading = false;
      }
    });
  }

  /**
   * Handle feedback user filter change from dropdown with debounce
   */
  onFeedbackUsersSelected(selectedUserIds: string[]): void {
    this.selectedUsers = selectedUserIds;
    
    // Clear existing debounce timer
    if (this.feedbackFilterDebounceTimer) {
      clearTimeout(this.feedbackFilterDebounceTimer);
    }
    
    // Set new debounce timer
    this.feedbackFilterDebounceTimer = setTimeout(() => {
      this.onFeedbackFilterChangeDebounced();
    }, this.DEBOUNCE_DELAY);
  }

  /**
   * Handle feedback rating filter change from dropdown with debounce
   */
  onFeedbackRatingsSelected(selectedRatings: any[]): void {
    // Keep ratings as strings for dropdown, but convert to numbers for API call
    // Store strings for dropdown display/state
    this.selectedRatings = selectedRatings;
    
    // Clear existing debounce timer
    if (this.feedbackFilterDebounceTimer) {
      clearTimeout(this.feedbackFilterDebounceTimer);
    }
    
    // Set new debounce timer
    this.feedbackFilterDebounceTimer = setTimeout(() => {
      this.onFeedbackFilterChangeDebounced();
    }, this.DEBOUNCE_DELAY);
  }

  /**
   * Get filtered active users based on search
   */
  getFilteredActiveUsers(): ActiveUser[] {
    if (!this.searchQuery) {
      return this.activeUsers;
    }
    const query = this.searchQuery.toLowerCase();
    return this.activeUsers.filter(user => 
      user.name.toLowerCase().includes(query) || 
      user.email.toLowerCase().includes(query)
    );
  }

  /**
   * Get paginated active users
   */
  getPaginatedActiveUsers(): ActiveUser[] {
    const filtered = this.getFilteredActiveUsers();
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return filtered.slice(startIndex, startIndex + this.itemsPerPage);
  }

  /**
   * Get total pages for active users
   */
  getTotalPages(): number {
    return Math.ceil(this.getFilteredActiveUsers().length / this.itemsPerPage);
  }

  /**
   * Next page
   */
  nextPage(): void {
    if (this.currentPage < this.getTotalPages()) {
      this.currentPage++;
    }
  }

  /**
   * Previous page
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  /**
   * Get star rating array for feedback (only filled stars)
   */
  getStarArray(rating: number): boolean[] {
    return Array(rating).fill(true);
  }

  /**
   * Get empty star rating array
   */
  getEmptyStarArray(rating: number): boolean[] {
    return Array(5 - rating).fill(false);
  }

  /**
   * Apply filters and reload feedback
   */
  applyFeedbackFilters(): void {
    this.loadFeedbackPage(1);
  }

  /**
   * Get filtered feedback based on user and rating selection
   * Note: Filtering is now done on the server side via API
   */
  getFilteredFeedback(): FeedbackComment[] {
    return this.feedbackComments;
  }

  /**
   * Toggle answer visibility for a feedback item
   */
  toggleAnswerVisibility(feedbackId: string): void {
    this.expandedAnswers[feedbackId] = !this.expandedAnswers[feedbackId];
  }

  /**
   * Check if answer is visible for a feedback item
   */
  isAnswerVisible(feedbackId: string): boolean {
    return this.expandedAnswers[feedbackId] || false;
  }

  /**
   * Get unique users from active users list for dropdown
   */
  getUniqueUsers(): ActiveUser[] {
    return this.activeUsers;
  }

  /**
   * Convert active users to dropdown options format
   */
  getUsersDropdownOptions() {
    return this.activeUsers.map(user => ({
      id: user.user_id,
      label: user.name
    }));
  }

  /**
   * Get ratings dropdown options
   */
  getRatingsDropdownOptions() {
    return [
      { id: '0', label: 'No Rating' },
      { id: '5', label: '★★★★★ (5 stars)' },
      { id: '4', label: '★★★★☆ (4 stars)' },
      { id: '3', label: '★★★☆☆ (3 stars)' },
      { id: '2', label: '★★☆☆☆ (2 stars)' },
      { id: '1', label: '★☆☆☆☆ (1 star)' }
    ];
  }

  /**
   * Format timestamp to readable format
   */
  /**
   * Format timestamp to show only date in IST
   * The timestamp from API is already in IST
   */
  formatTimestamp(timestamp: string): string {
    // Parse the timestamp (already in IST from API)
    const date = new Date(timestamp);
    
    // Format the timestamp as IST date only
    const formattedDate = date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });

    return formattedDate;
  }

  /**
   * Download data as PDF
   */
  downloadData(): void {
    if (!this.activeModal) return;

    let dataToDownload: any[] = [];
    let filename = '';
    let title = '';

    if (this.activeModal === 'users') {
      dataToDownload = this.activeUsers;
      filename = `active-users-${this.fromDate}-to-${this.toDate}.pdf`;
      title = 'Active Users Report';
      this.downloadUsersPDF(dataToDownload, title, filename);
    } else if (this.activeModal === 'questions') {
      dataToDownload = this.questionsAsked;
      filename = `questions-${this.fromDate}-to-${this.toDate}.pdf`;
      title = 'Questions Asked Report';
      this.downloadQuestionsPDF(dataToDownload, title, filename);
    } else if (this.activeModal === 'feedback') {
      dataToDownload = this.getFilteredFeedback();
      filename = `feedback-${this.fromDate}-to-${this.toDate}.pdf`;
      title = 'Feedback Comments Report';
      this.downloadFeedbackPDF(dataToDownload, title, filename);
    }
  }

  /**
   * Download Active Users as PDF
   */
  private downloadUsersPDF(data: ActiveUser[], title: string, filename: string): void {
    const doc = new jsPDF();
    let yPos = 20;
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;

    // Title
    doc.setFontSize(18);
    doc.setTextColor(11, 45, 113); // Dark blue color
    doc.text(title, margin, yPos);
    yPos += 12;

    // Date range and summary
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100); // Gray color
    doc.text(`Date Range: ${this.fromDate} to ${this.toDate}`, margin, yPos);
    yPos += 6;
    doc.text(`Total Users: ${data.length}`, margin, yPos);
    yPos += 12;

    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
    yPos += 5;

    // Users list
    doc.setFontSize(10);
    data.forEach((user, index) => {
      // Check if we need a new page
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }

      // User name
      doc.setTextColor(11, 45, 113);
      doc.setFont('', 'bold');
      doc.text(`${index + 1}. ${user.name}`, margin, yPos);
      yPos += 5;

      // Email
      doc.setFont('', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(9);
      doc.text(`Email: ${user.email}`, margin + 5, yPos);
      yPos += 8;

      doc.setFontSize(10);
    });

    doc.save(filename);
  }

  /**
   * Download Questions as PDF
   */
  private downloadQuestionsPDF(data: Question[], title: string, filename: string): void {
    const doc = new jsPDF();
    let yPos = 20;
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const maxWidth = pageWidth - (margin * 2);

    // Title
    doc.setFontSize(18);
    doc.setTextColor(11, 45, 113); // Dark blue color
    doc.text(title, margin, yPos);
    yPos += 12;

    // Date range and summary
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100); // Gray color
    doc.text(`Date Range: ${this.fromDate} to ${this.toDate}`, margin, yPos);
    yPos += 6;
    doc.text(`Total Questions: ${data.length}`, margin, yPos);
    yPos += 12;

    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
    yPos += 5;

    // Questions list
    doc.setFontSize(10);
    data.forEach((q, index) => {
      // Check if we need a new page
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      
      // Question number and content
      doc.setTextColor(11, 45, 113);
      doc.setFont('', 'bold');
      const questionLines = doc.splitTextToSize(`${index + 1}. ${q.content}`, maxWidth);
      questionLines.forEach((line: string) => {
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, margin, yPos);
        yPos += 5;
      });
      
      // User info and time
      doc.setFont('', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`User: ${q.name || q.email}`, margin + 5, yPos);
      yPos += 5;

      doc.setTextColor(150, 150, 150);
      doc.setFontSize(9);
      doc.text(`${this.formatTimestamp(q.timestamp)}`, margin + 5, yPos);
      yPos += 10;

      // Separator line
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, yPos - 3, pageWidth - margin, yPos - 3);
      yPos += 2;
    });

    doc.save(filename);
  }

  /**
   * Download Feedback as PDF (without answer, only question and comments)
   */
  private downloadFeedbackPDF(data: FeedbackComment[], title: string, filename: string): void {
    const doc = new jsPDF();
    let yPos = 20;
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const maxWidth = pageWidth - (margin * 2);

    // Title
    doc.setFontSize(18);
    doc.setTextColor(11, 45, 113); // Dark blue color
    doc.text(title, margin, yPos);
    yPos += 12;

    // Date range and summary
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100); // Gray color
    doc.text(`Date Range: ${this.fromDate} to ${this.toDate}`, margin, yPos);
    yPos += 6;
    doc.text(`Total Feedback: ${data.length}`, margin, yPos);
    yPos += 12;

    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
    yPos += 5;

    // Feedback list
    doc.setFontSize(10);
    data.forEach((feedback, index) => {
      // Check if we need a new page
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      
      // Item number and question
      doc.setTextColor(11, 45, 113);
      doc.setFont('', 'bold');
      const questionLines = doc.splitTextToSize(
        `${index + 1}. ${feedback.question}`, 
        maxWidth
      );
      questionLines.forEach((line: string, lineIndex: number) => {
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, margin, yPos);
        yPos += 5;
      });

      // Additional comments (if exists)
      if (feedback.additional_comments) {
        doc.setFont('', 'normal');
        doc.setTextColor(80, 80, 80);
        const commentLines = doc.splitTextToSize(
          `Comments: "${feedback.additional_comments}"`, 
          maxWidth - 5
        );
        commentLines.forEach((line: string) => {
          if (yPos > pageHeight - 30) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, margin + 5, yPos);
          yPos += 5;
        });
      }

      // Rating (just numbers, no stars)
      doc.setFont('', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Rating: ${feedback.response_rating}/5 | Sentiment: ${feedback.sentiment} | User: ${feedback.name || feedback.email}`, 
        margin + 5, 
        yPos
      );
      yPos += 6;

      // Time
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(9);
      doc.text(`${this.formatTimestamp(feedback.timestamp)}`, margin + 5, yPos);
      yPos += 10;

      // Separator line
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, yPos - 3, pageWidth - margin, yPos - 3);
      yPos += 2;
    });

    doc.save(filename);
  }
}
